import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Search, Upload, Pencil } from 'lucide-react';

// Conduct > Employee IDs (HR round, 27 Oct 2026). HR edits + imports from Keka; the MD
// (isHr=false here) gets a read-only view. Real names are never returned by the API —
// only an "On file / Missing" boolean — this component never has them to show either.
export default function EmployeeIdsTab({ isHr }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get('/conduct/hr/staff-ids', { params: { q } })).data); }
    catch (e) { toast.error('Failed to load Employee IDs'); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const filtered = rows.filter((r) => {
    if (filter === 'missing_id') return !r.staff_number;
    if (filter === 'missing_name') return !r.real_name_on_file;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="employee-ids-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-8" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="employee-ids-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[200px]" data-testid="employee-ids-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All employees</SelectItem>
            <SelectItem value="missing_id">Missing employee ID</SelectItem>
            <SelectItem value="missing_name">Missing real name</SelectItem>
          </SelectContent>
        </Select>
        {isHr && (
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="employee-ids-import-btn">
            <Upload className="h-4 w-4 mr-2" />Import from Keka
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Alias Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Profile</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Employee ID</TableHead>
                <TableHead>Real name</TableHead>
                {isHr && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No employees found</TableCell></TableRow>
              ) : filtered.map((r) => (
                <TableRow key={r.user_id} data-testid={`employee-ids-row-${r.email}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.email}</TableCell>
                  <TableCell className="text-xs">{r.profile_id || '—'}</TableCell>
                  <TableCell>{r.is_active ? <Badge variant="outline" className="rounded-sm">Active</Badge> : <Badge variant="outline" className="rounded-sm bg-muted">Inactive</Badge>}</TableCell>
                  <TableCell>{r.staff_number || <span className="text-amber-700 text-xs">Missing</span>}</TableCell>
                  <TableCell>{r.real_name_on_file ? <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">On file</Badge> : <Badge variant="outline" className="rounded-sm bg-amber-50 text-amber-800 border-amber-200">Missing</Badge>}</TableCell>
                  {isHr && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`employee-ids-edit-${r.email}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editing && <EditRowDialog row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {importOpen && <ImportDialog onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />}
    </div>
  );
}

function EditRowDialog({ row, onClose, onSaved }) {
  const [staffNumber, setStaffNumber] = useState(row.staff_number || '');
  const [realName, setRealName] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/conduct/hr/staff-ids/${row.user_id}`, { staff_number: staffNumber, real_name: realName });
      toast.success('Saved');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="employee-ids-edit-dialog">
        <DialogHeader><DialogTitle>{row.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Employee ID</Label>
            <Input value={staffNumber} onChange={(e) => setStaffNumber(e.target.value)} data-testid="employee-ids-edit-staff-number" />
          </div>
          <div className="space-y-1.5">
            <Label>Real name</Label>
            <Input value={realName} onChange={(e) => setRealName(e.target.value)} placeholder="" data-testid="employee-ids-edit-real-name" />
            <p className="text-xs text-muted-foreground">Enter to replace; it is never shown again.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="employee-ids-edit-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [conflictChoices, setConflictChoices] = useState({});
  const [unmatchedChoices, setUnmatchedChoices] = useState({});
  const [userOptions, setUserOptions] = useState([]);
  const [confirming, setConfirming] = useState(false);

  const runPreview = async () => {
    if (!file) return;
    setLoadingPreview(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/conduct/hr/staff-ids/import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      if (res.data.unmatched?.length) {
        const all = (await api.get('/conduct/hr/staff-ids')).data;
        setUserOptions(all);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to read the file');
    } finally { setLoadingPreview(false); }
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      const choices = {};
      Object.entries(conflictChoices).forEach(([idx, action]) => { choices[idx] = { action }; });
      Object.entries(unmatchedChoices).forEach(([idx, user_id]) => { if (user_id) choices[idx] = { user_id }; });
      const fd = new FormData();
      fd.append('file', file);
      fd.append('choices', JSON.stringify(choices));
      const res = await api.post('/conduct/hr/staff-ids/import/confirm', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const c = res.data.counts;
      const namesNote = c.names_skipped_no_key > 0
        ? ` (real names not stored for ${c.names_skipped_no_key} — real-name storage is not configured)` : '';
      toast.success(`Import complete — ${c.applied} applied, ${c.skipped} skipped${namesNote}`);
      onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.problems) {
        toast.error(`${detail.message} (rows: ${detail.problems.map((p) => p.row_index).flat().join(', ')})`);
      } else {
        toast.error(detail || 'Import failed');
      }
    } finally { setConfirming(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="employee-ids-import-dialog">
        <DialogHeader><DialogTitle>Import from Keka</DialogTitle></DialogHeader>
        {!preview ? (
          <div className="space-y-4">
            <Input type="file" accept=".xlsx,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="employee-ids-import-file" />
            <Button onClick={runPreview} disabled={!file || loadingPreview} data-testid="employee-ids-import-preview-btn">
              {loadingPreview && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Preview
            </Button>
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-4 gap-2 text-sm text-center">
              <div><div className="font-semibold text-lg">{preview.counts.matched}</div>Matched</div>
              <div><div className="font-semibold text-lg">{preview.counts.already_up_to_date}</div>Up to date</div>
              <div><div className="font-semibold text-lg">{preview.counts.conflicts}</div>Conflicts</div>
              <div><div className="font-semibold text-lg">{preview.counts.unmatched}</div>Unmatched</div>
            </div>

            {preview.conflicts.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Conflicts</p>
                {preview.conflicts.map((c) => (
                  <div key={c.row_index} className="flex items-center justify-between py-1.5 border-b text-sm gap-2" data-testid={`employee-ids-conflict-${c.row_index}`}>
                    {c.reason === 'id_belongs_to_someone_else' ? (
                      <span className="text-amber-800">
                        {c.email} → {c.staff_number}: this ID belongs to {c.alias_name || 'someone else'}; change that person first.
                      </span>
                    ) : (
                      <>
                        <span>{c.email} → {c.staff_number} (already has {c.current_staff_number})</span>
                        <RadioGroup value={conflictChoices[c.row_index] || 'keep'} onValueChange={(v) => setConflictChoices((p) => ({ ...p, [c.row_index]: v }))} className="flex gap-3">
                          <div className="flex items-center gap-1"><RadioGroupItem value="keep" id={`keep-${c.row_index}`} /><Label htmlFor={`keep-${c.row_index}`} className="text-xs">Keep</Label></div>
                          <div className="flex items-center gap-1"><RadioGroupItem value="overwrite" id={`overwrite-${c.row_index}`} data-testid={`employee-ids-conflict-overwrite-${c.row_index}`} /><Label htmlFor={`overwrite-${c.row_index}`} className="text-xs">Overwrite</Label></div>
                        </RadioGroup>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {preview.unmatched.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Unmatched — pick a user, or leave blank to skip</p>
                {preview.unmatched.map((u) => (
                  <div key={u.row_index} className="flex items-center justify-between py-1.5 border-b text-sm gap-2" data-testid={`employee-ids-unmatched-${u.row_index}`}>
                    <span>{u.email} → {u.staff_number}</span>
                    <Select value={unmatchedChoices[u.row_index] || ''} onValueChange={(v) => setUnmatchedChoices((p) => ({ ...p, [u.row_index]: v }))}>
                      <SelectTrigger className="w-[220px]" data-testid={`employee-ids-unmatched-select-${u.row_index}`}><SelectValue placeholder="Skip" /></SelectTrigger>
                      <SelectContent>
                        {userOptions.map((o) => <SelectItem key={o.user_id} value={o.user_id}>{o.name} ({o.email})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {preview && (
            <Button onClick={confirm} disabled={confirming} data-testid="employee-ids-import-confirm-btn">
              {confirming && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Confirm Import
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
